// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from '@reduxjs/toolkit';
import { addAsyncCases, updateStateStatus } from '../utils/utils';
import {
  fetchBlockEquipmentDetails,
  fetchCompanyEquipmentGroups,
  fetchEquipmentGroupById,
  fetchControlGroupById,
  fetchEquipmentDetails,
  fetchEquipmentGroupTypes,
  createEquipmentGroup,
  updateEquipmentGroup,
  deleteEquipmentGroup,
  deleteEquipmentGroupAndChildren,
  controlEquipment,
  fetchPresetsForFamilyTree,
  createPreset,
  updateEquipmentGroupItem,
  updatePresetImage,
  addConnection,
  removeConnection,
  controlEquipmentAction,
  updatePreset,
  deletePreset,
  applyPreset,
  stopPreset,
  stopAllPresetsInStudio,
  fetchPresetSequenceById,
  createPresetSequence,
  updatePresetSequence,
  deletePresetSequence,
  applyPresetSequence,
  stopPresetSequence,
  parkEquipmentGroup,
  updatePresetSequenceImage,
  updateButtonAssignments,
  lockEquipmentGroup,
  acceptLockRequest,
  rejectLockRequest,
  unlockEquipmentGroup,
  unlockAllEquipmentGroups,
  unlockAllEquipmentGroupLocks,
} from './equipmentActions';

import { updateCompanyEquipment } from '../companies/companiesActions';

import {
  fetchCompanyEquipmentGroupsState,
  createEquipmentGroupState,
  updateEquipmentGroupState,
  deleteEquipmentGroupState,
  updateCompanyEquipmentInGroupState,
  addLockOnEquipmentGroupState,
  updateLockOnEquipmentGroupState,
  deleteLockOnEquipmentGroupState,
  createPresetState,
  updatePresetState,
  deletePresetState,
  updatePresetManagerState,
  clearPresetState,
  selectEquipmentGroupState,
  selectCameraControlGroupState,
  createPresetSequenceState,
  updatePresetSequenceState,
  deletePresetSequenceState,
  createPresetSequenceStepState,
  updatePresetSequenceStepState,
  deletePresetSequenceStepState,
} from './equipmentReducers';

import { updateConfigObject } from './equipmentUtils';

const initialState = {
  selectedEquipment: null,
  selectedEquipmentGroup: null,
  cameraControlGroup: null,
  equipmentGroups: [],
  equipmentGroupTypes: [],
  presets: [],
  presetSequences: [],
  selectedPresetSequence: null,
  status: {
    selectedEquipment: 'idle',
    selectedEquipmentGroup: 'idle',
    cameraControlGroup: 'idle',
    equipmentGroups: 'idle',
    equipmentGroupTypes: 'idle',
    presets: 'idle',
    presetSequences: 'idle',
    selectedPresetSequence: 'idle',
  },
  error: {
    error: null,
    selectedEquipment: null,
    selectedEquipmentGroup: null,
    cameraControlGroup: null,
    equipmentGroups: null,
    equipmentGroupTypes: null,
    presets: null,
    presetSequences: null,
    selectedPresetSequence: null,
    clipBoardError: null,
  },
};

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    setSelectedEquipment: (state, action) => {
      const payload = action.payload || {};
      state.selectedEquipment = payload;
    },
    clearSelectedEquipment: state => {
      state.selectedEquipment = null;
    },
    clearSelectedEquipmentGroup: state => {
      state.selectedEquipmentGroup = null;
    },
    clearCameraControlGroup: state => {
      state.cameraControlGroup = null;
    },

    setError: (state, action) => {
      state.error.error = action.payload;
    },
    clearError: state => {
      state.error = initialState.error;
    },

    // websocket update for equipment properties and settings changes via websocket message EquipmentInStudioStateChangedFromApi
    wsUpdateEquipment: (state, action) => {
      const { equipmentUUID, settings, properties } = action.payload;

      if (!equipmentUUID) {
        return;
      }

      const updateEquipmentItem = item => {
        if (!item || item.co_equip_uuid !== equipmentUUID) {
          return;
        }

        if (settings && item.current_config?.settings) {
          updateConfigObject(item.current_config.settings, settings, '', 'settings');
        }

        if (properties && item.current_config?.properties) {
          updateConfigObject(item.current_config.properties, properties, '', 'properties');
        }

        item.last_updated = new Date().toISOString();
      };

      updateEquipmentItem(state.selectedEquipment);

      state.equipmentGroups?.forEach(group => {
        // direct equipment on group
        group?.equipment?.forEach(updateEquipmentItem);

        // mapped equipment (parent fallback case)
        group?.equipment_group_mappings?.forEach(mapping => {
          updateEquipmentItem(mapping?.company_equipment);
        });
      });
      state.selectedEquipmentGroup?.equipment?.forEach(updateEquipmentItem);
      state.cameraControlGroup?.equipment?.forEach(updateEquipmentItem);

      // Synchronise locks on equipment groups
      [state.selectedEquipmentGroup, state.cameraControlGroup].forEach(group => {
        if (group) {
          state.equipmentGroups
            ?.filter(eg => eg && eg.equip_group_uuid === group?.equip_group_uuid)
            .forEach(g => (group.lock = g ? [...g?.lock] : []));
        }
      });
    },
    // websocket modelDataChanged handlers
    wsCreateEquipmentGroup: (state, action) => {
      createEquipmentGroupState(state, action.payload);
    },

    wsUpdateEquipmentGroup: (state, action) => {
      const { equipmentGroup, companyEquipment } = action.payload;
      updateEquipmentGroupState(state, equipmentGroup, companyEquipment);
    },
    wsDeleteEquipmentGroup: (state, action) => {
      deleteEquipmentGroupState(state, action.payload);
    },
    wsCreatePreset: (state, action) => {
      createPresetState(state, action.payload);
    },
    wsUpdatePreset: (state, action) => {
      updatePresetState(state, action.payload);
    },
    wsDeletePreset: (state, action) => {
      const { preset_uuid: presetUuid } = action.payload;
      deletePresetState(state, presetUuid);
    },
    wsCreatePresetSequence: (state, action) => {
      createPresetSequenceState(state, action.payload);
    },
    wsUpdatePresetSequence: (state, action) => {
      updatePresetSequenceState(state, action.payload);
    },
    wsDeletePresetSequence: (state, action) => {
      const { preset_seq_uuid: presetSequenceUuid } = action.payload;
      deletePresetSequenceState(state, presetSequenceUuid);
    },
    wsAddLockOnEquipmentGroup: (state, action) => {
      addLockOnEquipmentGroupState(state, action?.payload);
    },
    wsUpdateLockOnEquipmentGroup: (state, action) => {
      updateLockOnEquipmentGroupState(state, action?.payload);
    },
    wsDeleteLockOnEquipmentGroup: (state, action) => {
      deleteLockOnEquipmentGroupState(state, action?.payload);
    },
    wsCreatePresetSequenceStep: (state, action) => {
      createPresetSequenceStepState(state, action.payload);
    },
    wsUpdatePresetSequenceStep: (state, action) => {
      updatePresetSequenceStepState(state, action.payload);
    },
    wsDeletePresetSequenceStep: (state, action) => {
      deletePresetSequenceStepState(state, action.payload);
    },
    // websocket preset manager updates
    wsPresetManagerUpdate: (state, action) => {
      updatePresetManagerState(state, action?.payload);
    },
    wsStudioEquipmentClearPreset: (state, action) => {
      clearPresetState(state, action?.payload);
    },
  },
  extraReducers: builder => {
    addAsyncCases(builder, [
      {
        action: fetchBlockEquipmentDetails,
        key: 'selectedEquipment',
      },
      { action: fetchEquipmentDetails, key: 'selectedEquipment' },

      {
        action: fetchEquipmentGroupTypes,
        key: 'equipmentGroupTypes',
      },
      {
        action: fetchPresetSequenceById,
        key: 'selectedPresetSequence',
      },
    ]);
    builder
      // equipment group
      .addCase(fetchCompanyEquipmentGroups.pending, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'loading');
      })
      .addCase(fetchCompanyEquipmentGroups.fulfilled, (state, action) => {
        fetchCompanyEquipmentGroupsState(state, action.payload);
        updateStateStatus(state, 'equipmentGroups', 'succeeded');
      })
      .addCase(fetchCompanyEquipmentGroups.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(fetchEquipmentGroupById.pending, state => {
        updateStateStatus(state, 'selectedEquipmentGroup', 'loading');
      })
      .addCase(fetchEquipmentGroupById.fulfilled, (state, action) => {
        selectEquipmentGroupState(state, action.payload);
        updateStateStatus(state, 'selectedEquipmentGroup', 'succeeded');
      })
      .addCase(fetchEquipmentGroupById.rejected, (state, action) => {
        updateStateStatus(state, 'selectedEquipmentGroup', 'failed', action);
      })
      .addCase(fetchControlGroupById.pending, state => {
        updateStateStatus(state, 'cameraControlGroup', 'loading');
      })
      .addCase(fetchControlGroupById.fulfilled, (state, action) => {
        selectCameraControlGroupState(state, action.payload);
        updateStateStatus(state, 'cameraControlGroup', 'succeeded');
      })
      .addCase(fetchControlGroupById.rejected, (state, action) => {
        updateStateStatus(state, 'cameraControlGroup', 'failed', action);
      })
      .addCase(createEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(updateEquipmentGroup.rejected, (state, action) => {
        state.error.clipBoardError = action?.payload || null;
        state.error.equipmentGroups = action?.payload?.message || '';
      })
      .addCase(deleteEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(deleteEquipmentGroupAndChildren.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(updateEquipmentGroupItem.rejected, (state, action) => {
        state.error.clipBoardError = action?.payload || null;
        state.error.equipmentGroups = action?.payload?.message || action?.payload?.msg || '';
      })
      // Update company equipment inside equipment group
      .addCase(updateCompanyEquipment.fulfilled, (state, action) => {
        updateCompanyEquipmentInGroupState(state, action.payload);
      })
      // error handling for connections
      .addCase(addConnection.rejected, (state, action) => {
        state.error.equipmentGroups = action?.payload?.message || action?.payload || '';
      })
      .addCase(removeConnection.rejected, (state, action) => {
        state.error.equipmentGroups = action?.payload?.message || action?.payload || '';
      })
      // error handling for equipment control
      .addCase(controlEquipment.rejected, (state, action) => {
        updateStateStatus(state, 'selectedEquipment', 'failed', action);
      })
      .addCase(controlEquipmentAction.rejected, (state, action) => {
        updateStateStatus(state, 'selectedEquipment', 'failed', action);
      })
      // error handling for equipment group locking
      .addCase(lockEquipmentGroup.rejected, (state, action) => {
        state.error.clipBoardError = action?.payload || null;
        state.error.equipmentGroups = action?.payload?.message || '';
      })
      .addCase(acceptLockRequest.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(rejectLockRequest.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(unlockEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(unlockAllEquipmentGroups.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      .addCase(unlockAllEquipmentGroupLocks.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      // Preset error handling
      .addCase(fetchPresetsForFamilyTree.pending, (state, action) => {
        updateStateStatus(state, 'presets', 'loading');
      })
      .addCase(fetchPresetsForFamilyTree.fulfilled, (state, action) => {
        state.presets = action.payload?.presets || [];
        state.presetSequences = action.payload?.preset_sequences || [];
        updateStateStatus(state, 'presets', 'succeeded');
      })
      .addCase(fetchPresetsForFamilyTree.rejected, (state, action) => {
        updateStateStatus(state, 'presets', 'failed', action);
      })
      .addCase(createPreset.rejected, (state, action) => {
        const { message } = action?.payload || {};
        updateStateStatus(state, 'presets', 'failed', message ?? action);
      })
      .addCase(updatePreset.rejected, (state, action) => {
        const { message } = action?.payload || {};
        updateStateStatus(state, 'presets', 'failed', message ?? action);
      })
      .addCase(deletePreset.rejected, (state, action) => {
        const { message } = action?.payload || {};
        updateStateStatus(state, 'presets', 'failed', message ?? action);
      })
      .addCase(applyPreset.rejected, (state, action) => {
        updateStateStatus(state, 'presets', 'failed', action);
      })
      .addCase(stopPreset.rejected, (state, action) => {
        updateStateStatus(state, 'presets', 'failed', action);
      })
      .addCase(stopAllPresetsInStudio.rejected, (state, action) => {
        updateStateStatus(state, 'presets', 'failed', action);
      })
      .addCase(updatePresetImage.rejected, (state, action) => {
        updateStateStatus(state, 'presets', 'failed', action);
      })
      // Preset Sequence error handling
      .addCase(createPresetSequence.rejected, (state, action) => {
        const { message } = action?.payload || {};
        updateStateStatus(state, 'presetSequences', 'failed', message ?? action);
      })
      .addCase(updatePresetSequence.rejected, (state, action) => {
        const { message } = action?.payload || {};
        updateStateStatus(state, 'selectedPresetSequence', 'failed', message ?? action);
      })
      .addCase(deletePresetSequence.rejected, (state, action) => {
        const { message } = action?.payload || {};
        updateStateStatus(state, 'selectedPresetSequence', 'failed', message ?? action);
      })
      .addCase(applyPresetSequence.rejected, (state, action) => {
        updateStateStatus(state, 'selectedPresetSequence', 'failed', action);
      })
      .addCase(stopPresetSequence.rejected, (state, action) => {
        updateStateStatus(state, 'selectedPresetSequence', 'failed', action);
      })
      .addCase(updatePresetSequenceImage.rejected, (state, action) => {
        updateStateStatus(state, 'selectedPresetSequence', 'failed', action);
        updateStateStatus(state, 'presetSequences', 'failed', action);
      })
      // Park equipment group error handling
      .addCase(parkEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, 'equipmentGroups', 'failed', action);
      })
      // Button Assignment error handling
      .addCase(updateButtonAssignments.rejected, (state, action) => {
        updateStateStatus(state, 'selectedEquipment', 'failed', action);
      });
  },
});

export const {
  setSelectedEquipment,
  clearSelectedEquipment,
  clearSelectedEquipmentGroup,
  setError,
  clearError,
  clearCameraControlGroup,
  wsUpdateEquipment,
  wsPresetManagerUpdate,
  wsCreateEquipmentGroup,
  wsUpdateEquipmentGroup,
  wsDeleteEquipmentGroup,
  wsCreatePreset,
  wsUpdatePreset,
  wsDeletePreset,
  wsCreatePresetSequence,
  wsUpdatePresetSequence,
  wsDeletePresetSequence,
  wsAddLockOnEquipmentGroup,
  wsUpdateLockOnEquipmentGroup,
  wsDeleteLockOnEquipmentGroup,
  wsCreatePresetSequenceStep,
  wsUpdatePresetSequenceStep,
  wsDeletePresetSequenceStep,
  wsStudioEquipmentClearPreset,
} = equipmentSlice.actions;

export const equipmentStatus = state => state.equipment.status;
export const equipmentError = state => state.equipment.error;

export default equipmentSlice.reducer;
